import { usePlaceholderContent } from '@eneco/cms';
import type { CmsRendering } from '@eneco/cms-adapter';
import type { CmsContentTypes } from '@eneco/cms-types/generated';

/**
 * A flow step's own rendering, found among its siblings by component name.
 *
 * Steps are the one part of the front end that cannot use `useContent`. Every
 * other component is mounted by a placeholder, which puts its rendering into
 * context on the way in. A step is mounted by the flow's own router instead, so
 * nothing hands it anything and it has to go looking.
 *
 * What it looks in is the page's application placeholder (`jss-app-*`), which
 * holds one rendering per step name. That is a page-level read, so it works the
 * same on either provider: the Sitecore adapter maps the route's placeholders
 * straight through, and the Sanity adapter reconstructs them from each block's
 * `area`.
 *
 * Returns `undefined` when the page carries nothing under that name, which is
 * what an unauthored step looks like.
 *
 * ⚠ `uid` is NOT a per-step-page identity, despite what this comment used to
 * claim. It names the rendering *definition* on the layout, and every step page
 * in a flow reuses the same one, so keying a step on it never remounts
 * anything. Sitecore's `dataSource` did differ, because it named the content
 * item. Use {@link useStepKey} for a remount key.
 */
export const useStepRendering = (componentName: string): CmsRendering | undefined =>
  usePlaceholderContent<Record<string, CmsRendering | undefined>>()[componentName];

/**
 * The flat content of a flow step's own rendering, typed from its component name.
 *
 * The type is the component's generated content type (`@eneco/cms-types/generated`),
 * taken from the CMS team's component map: every field under the name both
 * adapters give it, all optional. The CMS layer does not check what arrived, so
 * the type is the CMS's contract, not a guarantee of this payload. A name the map
 * does not have gives `unknown`, so reading a field from it does not compile.
 */
export function useStepContent<Name extends keyof CmsContentTypes>(
  componentName: Name,
): CmsContentTypes[Name] | undefined;
/**
 * @deprecated Pass only the component name, and the content type follows from
 * it. This form, where the step names a type of its own, is for steps that have
 * not moved to the generated types yet; it goes once the last one has.
 */
export function useStepContent<T>(componentName: string): T | undefined;
export function useStepContent(componentName: string): unknown {
  return useStepRendering(componentName)?.content;
}
